<script setup lang="ts">
import { ref, watch, onBeforeMount, computed } from 'vue';
import { useEditUser, useUsersExists, useGetUsers, CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import type { UserOutput } from '@ilteducation/customer-mgmt-types';
import { Role } from '../../types/user';
import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import { usePermissions } from '../../composables/usePermissions';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import useUserValidation from '../UserSidePanel/EditUser/useUserValidation';
import { RoleOption, useUserRoles } from '../../composables/useUserRoles';
import RoleSelect from '../RoleSelect.vue';
import { checkQrCodeEditablePermission, isGroupLevelRestriction } from '@ilteducation/customer-mgmt-permissions';
import { generateRandomPassword, generateQrToken } from '../../utils';
import useModal, { modals } from '../../composables/useModal';
import UserExistsModal from './UserExistsModal.vue';
import GroupAssignment from './GroupAssignment.vue';
import { useGotoProfile } from '../../composables/useGotoProfile';
import { required } from '../../rules';
import { useMyGroups, useMyOrganizations } from '@ilteducation/my-account';

const { t } = useLocale();
type FormUser = {
  firstName?: string;
  lastName?: string;
  role: Role | null;
  email?: string;
  userName?: string;
  groupUid?: string;
  password?: string;
  sharedAccount: boolean;
};

type FormPayload = {
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  userName?: string;
  password: string;
  role: Role;
  sharedAccount: boolean;
  qrToken?: string;
  groupUid?: string;
};

type Props = {
  organization: SlimOrganization;
  loading?: boolean;
};

const props = defineProps<Props>();

const { gotoUserProfile } = useGotoProfile();
const { groups, loadMyGroups, isLoading: groupsLoading } = useMyGroups();
const { organizations, isLoading: organizationsLoading } = useMyOrganizations();

onBeforeMount(() => {
  loadMyGroups();
});

const { getRoleOptionsToCreateWith } = useUserRoles();
const roleOptions = ref<RoleOption[]>([]);
const rolesLoading = computed(() => groupsLoading.value || organizationsLoading.value);
watch(
  [props.organization, groups, organizations],
  async () => {
    const orgGroups = groups.value?.data.filter((g) => g.parent.uid === props.organization.uid);
    return getRoleOptionsToCreateWith(props.organization, orgGroups).then((options) => {
      roleOptions.value = options;
    });
  },
  { immediate: true },
);
const { permissionContext, getCallerAssociation, reporterBoolean } = usePermissions();

const formRef = ref();
const message = ref('');
const error = ref(false);
const isCreatingUser = ref(false);
const { rules, formUser } = useUserValidation<FormUser>();
const usernameError = ref<string | undefined>(undefined);
const { createUser } = useEditUser();
const { checkUsersExistsByUsernames } = useUsersExists();
const { getUsers } = useGetUsers();
const { toast } = useToast();
const isUsernameChangedByUser = ref<boolean>(false);
const showPassword = ref<boolean>(false);
const conflictUser = ref<UserOutput | undefined>(undefined);

const isGroupLevel = computed(() => isGroupLevelRestriction(props.organization));
const groupLabel = computed(() =>
  isGroupLevel.value ? `${t('lcp.group')} *` : `${t('lcp.group')} (${t('lcp.optional')})`,
);

const { open: openUserExistsModal, close: closeConflictModal } = useModal(modals.USER_EXISTS_CONFLICT);
const { close: closeThisModal } = useModal(modals.CREATE_SINGLE_USER);

const cloneUserData = () => {
  return {
    firstName: undefined,
    lastName: undefined,
    role: null,
    email: undefined,
    userName: undefined,
    password: undefined,
    sharedAccount: false,
    groupUid: undefined,
  };
};
onBeforeMount(() => {
  formUser.value = cloneUserData();
});

export type NewUser = {
  uid: string;
  name: string;
  password: string;
  qrToken?: string;
  role: Role;
  sharedAccount: boolean;
};

const isQrEnabledForUserRole = async (role: Role, organization: SlimOrganization) =>
  await checkQrCodeEditablePermission(
    permissionContext.value,
    { organization, role },
    getCallerAssociation,
    reporterBoolean,
  );

const buildPayload = async (): Promise<FormPayload> => {
  const role = formUser.value.role ?? 'student'; // should never fall back due to validation.
  const password = formUser.value.password || generateRandomPassword();
  const qrToken = (await isQrEnabledForUserRole(role, props.organization)) ? generateQrToken() : undefined;
  const firstName = formUser.value.firstName?.trim();
  const lastName = formUser.value.lastName?.trim() || undefined;
  const userName = formUser.value.userName?.trim().toLowerCase() || undefined;
  const groupUid = formUser.value.groupUid || undefined;

  return {
    name: [firstName, lastName].filter(Boolean).join(' '),
    firstName,
    lastName,
    email: formUser.value.email?.trim().toLowerCase() || undefined,
    userName,
    password,
    role,
    sharedAccount: formUser.value.sharedAccount,
    qrToken,
    groupUid,
  };
};

const createSingleUser = async (): Promise<NewUser | null> => {
  error.value = false;
  message.value = '';
  conflictUser.value = undefined;
  isCreatingUser.value = true;

  const { valid } = await formRef.value.validate();
  if (!valid) {
    isCreatingUser.value = false;
    return null;
  }

  const payload = await buildPayload();

  // Entering username for students are not required. it will be auto generated.
  if (payload.userName) {
    const [existsResult, usernameSearchResult] = await Promise.all([
      checkUsersExistsByUsernames([payload.userName]),
      getUsers({ filters: { username: payload.userName } }),
    ]);

    const usernameExists = payload.userName ? (existsResult?.[payload.userName] ?? false) : false;

    const foundUser: UserOutput | undefined = usernameSearchResult?.data[0];
    if (foundUser) {
      conflictUser.value = foundUser;
      openUserExistsModal();
      isCreatingUser.value = false;
      return null;
    }

    if (usernameExists) {
      usernameError.value = t('lcp.username_taken_in_unreachable_scope');
      isCreatingUser.value = false;
      return null;
    }
  }

  const memberships = payload.groupUid ? { memberships: [{ groupUid: payload.groupUid }] } : {};

  const createdUser = await createUser({
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    password: payload.password,
    userName: payload.userName,
    guest: payload.sharedAccount,
    qrToken: payload.qrToken,
    schoolAssociations: [{ schoolUid: props.organization.uid, role: payload.role, ...memberships }],
  } as CustomerMgmtTypes.CreateUserRequest);

  if (createdUser === null) {
    isCreatingUser.value = false;
    error.value = true;
    message.value = t('lcp.failed_to_create_user');
    return null;
  }

  isCreatingUser.value = false;

  toast(t('lcp.user_created_successfully'), 'success');

  return {
    ...createdUser,
    name: payload.name,
    password: payload.password,
    qrToken: payload.qrToken,
    role: payload.role,
    sharedAccount: payload.sharedAccount,
  };
};

const isNonStudent = computed(() => formUser.value.role !== 'student');
const fieldLabel = (key: string) => (isNonStudent.value ? `${t(key)} *` : `${t(key)} (${t('lcp.optional')})`);

const syncUsernameWithEmail = () => {
  if (isUsernameChangedByUser.value) return;
  formUser.value.userName = formUser.value.email;
};

const onUsernameChange = () => {
  isUsernameChangedByUser.value = true;
  usernameError.value = undefined;
};

const onEmailChange = () => {
  syncUsernameWithEmail();
};

const onRoleSelectUpdate = () => {
  syncUsernameWithEmail();
};

const onUseDifferentUsername = () => {
  usernameError.value = t('lcp.username_taken_choose_a_different_username');
  conflictUser.value = undefined;
};

const gotUserProfile = (userUid: string) => {
  conflictUser.value = undefined;
  closeConflictModal();
  closeThisModal();
  gotoUserProfile(userUid);
};

const isLoading = computed(() => props.loading || isCreatingUser.value);

// The menu hands its activator props to a text field, and Vuetify routes everything but class,
// style and id to the <input> inside it. A textbox has no expanded state to carry — aria-haspopup
// and aria-controls it does — so that one attribute goes, and the menu wiring is untouched.
const fieldActivatorProps = ({ 'aria-expanded': _expanded, ...props }: Record<string, unknown>) => props;

const usernameRules = computed(() => [rules.email, ...(isNonStudent.value ? [rules.required] : [])]);

defineExpose({
  createUser: createSingleUser,
});
</script>

<template>
  <v-form ref="formRef" v-bind="$attrs" :disabled="isLoading" validate-on="submit">
    <v-row>
      <v-col>
        <RoleSelect
          v-model="formUser.role"
          :options="roleOptions"
          :placeholder-title="t('lcp.select_role')"
          :rules="[rules.required, rules.role]"
          variant="outlined"
          density="default"
          color="primary"
          hide-details="auto"
          :loading="rolesLoading"
          @update:modelValue="onRoleSelectUpdate"
          :label="`${t('lcp.role')} *`"
          required
        ></RoleSelect>
      </v-col>
      <v-col>
        <GroupAssignment
          :organization-uid="organization.uid"
          v-model:model-value="formUser.groupUid"
          :label="groupLabel"
          :disabled="isLoading"
          variant="field"
        >
          <template #activator="{ props: ap }">
            <v-text-field
              variant="outlined"
              :singleLine="false"
              color="primary"
              density="default"
              v-bind="fieldActivatorProps(ap.activatorProps)"
              clearable
              clear-icon="mdi-close"
              :label="groupLabel"
              block
              hide-details="auto"
              v-model="ap.selectedName"
              readonly
              :rules="isGroupLevel ? [required(t)] : []"
              @click="ap.onClick"
              @click:clear="ap.onClear"
            ></v-text-field>
          </template>
        </GroupAssignment>
      </v-col>
      <v-col cols="auto" class="d-flex">
        <v-tooltip :text="t('lcp.shared_account')" location="top">
          <template #activator="{ props }">
            <v-field
              :label="t('lcp.shared_account')"
              variant="outlined"
              color="primary"
              active
              :disabled="isLoading"
              class="d-flex align-center px-8"
            >
              <v-switch
                v-model="formUser.sharedAccount"
                color="primary"
                hide-details
                inset
                density="compact"
                :disabled="isLoading"
                :aria-label="t('lcp.shared_account')"
                v-bind="props"
                @click.stop
              ></v-switch>
            </v-field>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          :singleLine="false"
          color="primary"
          density="default"
          clearable
          clear-icon="mdi-close"
          :label="`${t('lcp.first_name')} *`"
          required
          block
          hide-details="auto"
          v-model="formUser.firstName"
          :rules="[rules.firstName, rules.required, rules.htmlSafe]"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          :singleLine="false"
          color="primary"
          density="default"
          clearable
          clear-icon="mdi-close"
          :label="`${t('lcp.last_name')} (${t('lcp.optional')})`"
          v-model="formUser.lastName"
          hide-details="auto"
          :rules="[rules.lastName, rules.htmlSafe]"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          :singleLine="false"
          clearable
          clear-icon="mdi-close"
          color="primary"
          hide-details="auto"
          density="default"
          :label="fieldLabel('lcp.email')"
          v-model="formUser.email"
          :rules="usernameRules"
          type="email"
          @update:model-value="onEmailChange"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          clearable
          :singleLine="false"
          clear-icon="mdi-close"
          color="primary"
          density="default"
          :label="fieldLabel('lcp.username')"
          v-model="formUser.userName"
          :rules="[rules.userName, ...(isNonStudent ? [rules.required] : [])]"
          :error-messages="usernameError"
          :hint="!isNonStudent ? t('lcp.autogenerated_if_left_empty') : undefined"
          :persistent-hint="!isNonStudent"
          @keydown="onUsernameChange"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          v-model="formUser.password"
          :type="showPassword ? 'text' : 'password'"
          :label="`${t('lcp.password')} (${t('lcp.optional')})`"
          variant="outlined"
          density="default"
          color="primary"
          :rules="[rules.password]"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword"
          :hint="t('lcp.autogenerated_if_left_empty')"
          persistent-hint
          autocomplete="new-password"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-alert v-if="message" :type="error ? 'error' : 'success'" variant="outlined" border="start" class="mb-6">
      {{ message }}
    </v-alert>
  </v-form>

  <UserExistsModal
    v-if="conflictUser && formUser.role"
    :name="modals.USER_EXISTS_CONFLICT"
    :user="conflictUser"
    :organization="organization"
    :role="formUser.role"
    @use-different-username="onUseDifferentUsername"
    @go-to-user-profile="gotUserProfile"
  />
</template>
